import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Mock Service for Provider/Location Search Testing
 * 
 * ⚠️ WARNING: This service is for DEVELOPMENT TESTING ONLY
 * It will NOT work in production environments (test, poc, prod)
 * 
 * Purpose: Allows mobile component testing without backend API dependency
 * Usage: Set USE_MOCK_DATA = true in environment.ts (local only)
 */
@Injectable({
  providedIn: 'root'
})
export class SearchMockService {
  
  // Safety check: Only allow mock data in local development
  private get isLocalDevelopment(): boolean {
    return !environment.production && 
           typeof window !== 'undefined' && 
           (window.location.hostname === 'localhost' || 
            window.location.hostname === '127.0.0.1');
  }

  /**
   * Check if mock service should be used
   * Must be explicitly enabled AND running locally
   */
  shouldUseMock(): boolean {
    if (!this.isLocalDevelopment) {
      console.error('🚫 Mock service attempted in non-local environment - BLOCKED');
      return false;
    }
    
    // Check for explicit enablement flag
    const useMock = (environment as any).useMockSearchData === true;
    
    if (useMock) {
      console.warn('⚠️ Using MOCK search data - Local development only');
    }
    
    return useMock;
  }

  /**
   * Mock Provider Search Results
   */
  getProviderSearchResults(query: { nameOrSpecialty: string; cityOrZip: string }): Observable<any[]> {
    if (!this.shouldUseMock()) {
      throw new Error('Mock service not available in this environment');
    }

    const mockProviders = [
      {
        id: 'MOCK-PROV-001',
        providerID: 'MOCK-PROV-001',
        providerName: 'Leah Phan, DO',
        specialties: 'Primary Care Provider',
        photo: null,
        address1: '4755 Katella Ave., Suite 103',
        city: 'Cypress',
        state: 'CA',
        zip: '90720',
        phone: '833-968-2758',
        distanceInMiles: 5.4,
        nextAvailability: 'Tomorrow 11AM',
        acceptingNewPatients: true,
        availableAppointments: 5
      },
      {
        id: 'MOCK-PROV-002',
        providerID: 'MOCK-PROV-002',
        providerName: 'Razieh Hadian Jazi, MD',
        specialties: 'Family Medicine Primary Care Provider',
        photo: null,
        address1: '4755 Katella Ave., Suite 103',
        city: 'Cypress',
        state: 'CA',
        zip: '90720',
        phone: '833-968-2758',
        distanceInMiles: 7.4,
        nextAvailability: 'August 8 at 11AM',
        acceptingNewPatients: true,
        availableAppointments: 2
      },
      {
        id: 'MOCK-PROV-003',
        providerID: 'MOCK-PROV-003',
        providerName: 'Gwendolyn S Gore, MD',
        specialties: 'Primary Care Provider',
        photo: null,
        address1: '4755 Katella Ave., Suite 103',
        city: 'Cypress',
        state: 'CA',
        zip: '90720',
        phone: '833-968-2758',
        distanceInMiles: 8.4,
        nextAvailability: null,
        acceptingNewPatients: false,
        availableAppointments: 0
      },
      {
        id: 'MOCK-PROV-004',
        providerID: 'MOCK-PROV-004',
        providerName: 'John W Wang, MD',
        specialties: 'Internal Medicine, Primary Care',
        photo: null,
        address1: '18035 Brookhurst Street, Suite 2100',
        city: 'Fountain Valley',
        state: 'CA',
        zip: '92708',
        phone: '833-968-2758',
        distanceInMiles: 7.4,
        nextAvailability: null,
        acceptingNewPatients: true,
        availableAppointments: 8
      }
    ];

    // Simulate API delay
    return of(mockProviders).pipe(delay(800));
  }

  /**
   * Mock Location Search Results
   */
  getLocationSearchResults(query: { serviceOrLocationType: string; cityOrZip: string }): Observable<any[]> {
    if (!this.shouldUseMock()) {
      throw new Error('Mock service not available in this environment');
    }

    const mockLocations = [
      {
        id: 'MOCK-LOC-001',
        name: 'Seattle Medical Center',
        type: 'Hospital',
        address: {
          line1: '1000 Main Street',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98101'
        },
        phone: '(206) 555-0001',
        distance: 1.2,
        services: ['Emergency Care', 'Surgery', 'Imaging', 'Laboratory'],
        hours: 'Open 24/7'
      },
      {
        id: 'MOCK-LOC-002',
        name: 'Northgate Urgent Care',
        type: 'Urgent Care',
        address: {
          line1: '2500 Northgate Way',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98125'
        },
        phone: '(206) 555-0002',
        distance: 3.5,
        services: ['Walk-in Care', 'X-Ray', 'Lab Tests'],
        hours: 'Mon-Sun 8am-8pm'
      },
      {
        id: 'MOCK-LOC-003',
        name: 'Capitol Hill Imaging Center',
        type: 'Imaging Center',
        address: {
          line1: '1234 Broadway Ave',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98122'
        },
        phone: '(206) 555-0003',
        distance: 2.8,
        services: ['MRI', 'CT Scan', 'Ultrasound', 'X-Ray'],
        hours: 'Mon-Fri 7am-7pm, Sat 8am-5pm'
      },
      {
        id: 'MOCK-LOC-004',
        name: 'Bellevue Primary Care Clinic',
        type: 'Clinic',
        address: {
          line1: '500 108th Ave NE',
          city: 'Bellevue',
          state: 'WA',
          zipCode: '98004'
        },
        phone: '(425) 555-0004',
        distance: 6.7,
        services: ['Primary Care', 'Vaccinations', 'Preventive Care'],
        hours: 'Mon-Fri 8am-5pm'
      },
      {
        id: 'MOCK-LOC-005',
        name: 'Eastside Laboratory Services',
        type: 'Laboratory',
        address: {
          line1: '789 Medical Parkway',
          city: 'Redmond',
          state: 'WA',
          zipCode: '98052'
        },
        phone: '(425) 555-0005',
        distance: 8.9,
        services: ['Blood Tests', 'Specimen Collection', 'Drug Screening'],
        hours: 'Mon-Fri 6am-6pm, Sat 7am-3pm'
      }
    ];

    // Simulate API delay
    return of(mockLocations).pipe(delay(800));
  }

  /**
   * Check if mock authentication is enabled
   */
  isMockAuthEnabled(): boolean {
    const mockAuth = (environment as any).mockAuth;
    return mockAuth?.enabled === true;
  }

  /**
   * Get mock user gender
   */
  getMockUserGender(): 'female' | 'male' {
    const mockAuth = (environment as any).mockAuth;
    return mockAuth?.gender || 'female';
  }

  /**
   * Mock Provider Quick Search Options
   * Returns gender-specific bubbles if authenticated, empty array if not
   */
  getProviderQuickSearchOptions(): Observable<any[]> {
    if (!this.shouldUseMock()) {
      throw new Error('Mock service not available in this environment');
    }

    // Only show bubbles if mock auth is enabled
    if (!this.isMockAuthEnabled()) {
      console.log('🔓 Mock auth disabled - no quick search bubbles');
      return of([]).pipe(delay(300));
    }

    const gender = this.getMockUserGender();
    console.log(`👤 Mock user gender: ${gender} - showing ${gender}-specific bubbles`);

    // Female-specific quick search options
    const femaleOptions = [
      { 
        name: 'Mammogram', 
        specialtyList: ['Radiology', 'Breast Imaging'],
        id: 'female-01'
      },
      { 
        name: 'OB/GYN Annual Exam', 
        specialtyList: ['Obstetrics & Gynecology'],
        id: 'female-02'
      },
      { 
        name: 'Prenatal Care', 
        specialtyList: ['Obstetrics'],
        id: 'female-03'
      },
      { 
        name: 'Birth Control', 
        specialtyList: ['Obstetrics & Gynecology', 'Family Medicine'],
        id: 'female-04'
      },
      { 
        name: 'Bone Density Test', 
        specialtyList: ['Endocrinology', 'Rheumatology'],
        id: 'female-05'
      }
    ];

    // Male-specific quick search options
    const maleOptions = [
      { 
        name: 'Prostate Screening', 
        specialtyList: ['Urology'],
        id: 'male-01'
      },
      { 
        name: 'Testosterone Check', 
        specialtyList: ['Endocrinology', 'Urology'],
        id: 'male-02'
      },
      { 
        name: 'Cardiology Checkup', 
        specialtyList: ['Cardiology'],
        id: 'male-03'
      },
      { 
        name: 'Sports Physical', 
        specialtyList: ['Sports Medicine', 'Family Medicine'],
        id: 'male-04'
      },
      { 
        name: 'Sleep Apnea', 
        specialtyList: ['Sleep Medicine', 'Pulmonology'],
        id: 'male-05'
      }
    ];

    const options = gender === 'male' ? maleOptions : femaleOptions;
    return of(options).pipe(delay(300));
  }

  /**
   * Mock Provider Details
   * Returns detailed information for a specific provider
   */
  getProviderDetails(providerId: string): any {
    if (!this.shouldUseMock()) {
      throw new Error('Mock service not available in this environment');
    }

    console.log('📋 Loading MOCK provider details for:', providerId);

    // Mock provider details with locations
    const mockDetails: any = {
      providerID: providerId,
      providerEmrId: 'EMR-' + providerId,
      providerName: 'Dr. Sarah Johnson, MD',
      specialties: 'Family Medicine, Internal Medicine',
      photo: 'https://mymemorialcare.memorialcare.org/MyChart/en-US/images/vudprovider.png',
      photoUrl: 'https://mymemorialcare.memorialcare.org/MyChart/en-US/images/vudprovider.png',
      about: '<p>Dr. Sarah Johnson is a board-certified family medicine physician with over 15 years of experience. She is passionate about providing comprehensive, patient-centered care to individuals and families in the Seattle area.</p><p>Dr. Johnson believes in building long-term relationships with her patients and focuses on preventive care, chronic disease management, and health education.</p>',
      providerEducations: [
        {
          label: 'Medical School',
          institution: 'University of Washington School of Medicine'
        },
        {
          label: 'Residency',
          institution: 'Swedish Medical Center - Family Medicine'
        },
        {
          label: 'Fellowship',
          institution: 'UW Medicine - Primary Care'
        }
      ],
      languages: 'English, Spanish',
      affiliations: 'Seattle Medical Group, Northwest Primary Care Network',
      nextAvailability: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days from now
      acceptingNewPatients: true,
      locations: [
        {
          practiceName: 'Seattle Medical Center - Downtown',
          address1: '1234 Medical Plaza',
          address2: 'Suite 200',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98101',
          phone: '(206) 555-0001',
          distanceInMiles: 2.3,
          locationDirection: 'https://maps.google.com/?q=1234+Medical+Plaza+Seattle+WA',
          officeHours: 'Mon-Fri 8am-6pm',
          photo: ''
        },
        {
          practiceName: 'Seattle Medical Center - Capitol Hill',
          address1: '5678 Broadway Ave E',
          address2: '',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98102',
          phone: '(206) 555-0002',
          distanceInMiles: 3.8,
          locationDirection: 'https://maps.google.com/?q=5678+Broadway+Ave+E+Seattle+WA',
          officeHours: 'Mon-Thu 9am-5pm, Fri 9am-3pm',
          photo: ''
        },
        {
          practiceName: 'Northgate Clinic',
          address1: '9012 Northgate Way',
          address2: 'Building A',
          city: 'Seattle',
          state: 'WA',
          zipCode: '98125',
          phone: '(206) 555-0003',
          distanceInMiles: 7.2,
          locationDirection: 'https://maps.google.com/?q=9012+Northgate+Way+Seattle+WA',
          officeHours: 'Mon-Fri 8am-5pm',
          photo: ''
        }
      ]
    };

    return mockDetails;
  }
}
